import { ref, useContext, readonly } from '@nuxtjs/composition-api';
import { Logger } from '@wemade-vsf/core';
import type { Ref } from '@nuxtjs/composition-api';
import type { FittingListEntry, Part, Brand, Model } from '../../types';
import chunk from 'lodash-es/chunk';

interface UsePartsBySkuInterface {
  loading: Readonly<Ref<boolean>>;
  error: Readonly<Ref<{
    load: Error | null
  }>>;

  load(): Promise<FittingListEntry[]>;
}
export function usePartsBySku (sku: string): UsePartsBySkuInterface {
  const loading = ref(false);
  const error = ref({
    load: null
  });

  const { app } = useContext();

  async function load(): Promise<FittingListEntry[]> {
    Logger.debug('usePartsBySku/load', sku);
    let result = [];
    loading.value = true;
    try {
      const { data } = await app.$vsf.$magento.api.pfPartsBySku({ sku, pageSize: 2000, currentPage: 1 });
      Logger.debug('[Result]: ', data);
      if (data?.WmPartFinderParts?.items?.length) {
        result = await buildFittingList(data.WmPartFinderParts.items);
      }
    } catch (err) {
      error.value.load = err;
      Logger.error('usePartsBySku/load', err);
    } finally {
      loading.value = false;
    }

    return result;
  }

  async function buildFittingList(items: Part[]): Promise<FittingListEntry[]> {
    const modelIds = new Set([])
    const brandIds = new Set([]);
    items.forEach(i => {
      modelIds.add("" + i.model_id);
      brandIds.add("" + i.brand_id);
    })
    const { data } = await app.$vsf.$magento.api.pfFinders({ pageSize: 100, currentPage: 1 });
    const finders = data?.WmPartFinderFinders?.items ?? [];
    const { brands, models } = await loadAllList(modelIds, brandIds); 
    const result = [];

    items.forEach(item => {
      const brand = brands.find(b => b.brand_id === item.brand_id);
      const model = models.find(m => m.model_id === item.model_id);
      const finder = finders.find(f => f.finder_id === brand.finder_id);

      if (!finder || !brand || !model) return;
        
      result.push({
        brand,
        model,
        finder,
        notes: item.notes
      })
    });
   

    return result;
  }

  async function loadAllList(modelIds: Set<string>, brandIds: Set<string>): Promise<{ brands: Brand[], models: Model[] }> {
    const [{ data: brandsData }, { data: modelsData }] = await Promise.all([
      loadBrands([...brandIds]),
      loadModels([...modelIds])
    ])

    const brands = brandsData?.WmPartFinderBrands?.items ?? [];
    const models = modelsData?.WmPartFinderModels?.items ?? [];
    return { brands, models }
  }

  async function loadBrands(brandIds: string[]) {
    if (brandIds.length <= 100) {
      return app.$vsf.$magento.api.pfBrands({ pageSize: brandIds.length, currentPage: 1, filter: { brand_id: { in: brandIds } } });
    } else {
      const result = [];
      const chunks = chunk(brandIds, 100);
      for (const chunk of chunks) {
        const { data } = await app.$vsf.$magento.api.pfBrands({ pageSize: chunk.length, currentPage: 1, filter: { brand_id: { in: chunk } } });
        result.push(...data?.WmPartFinderBrands?.items ?? []);
      }
      return { data: { WmPartFinderBrands: { items: result } } };
    }
  }

  async function loadModels(modelIds: string[]) {
    if (modelIds.length <= 100) {
      return app.$vsf.$magento.api.pfModels({ pageSize: modelIds.length, currentPage: 1, filter: { model_id: { in: modelIds } } });
    } else {
      const result = [];
      const chunks = chunk(modelIds, 100);
      for (const chunk of chunks) {
        const { data } = await app.$vsf.$magento.api.pfModels({ pageSize: chunk.length, currentPage: 1, filter: { model_id: { in: chunk } } });
        result.push(...data?.WmPartFinderModels?.items ?? []);
      }
      return { data: { WmPartFinderModels: { items: result } } };
    }
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    load
  }
}